import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type {
  AccountData,
  AccountLedgerRowData,
  AttachmentFileData,
  JournalAttachmentData,
  JournalEntryData,
} from '@/types/api';

const ACCOUNTS_KEY = ['accounting', 'accounts'] as const;
const ENTRIES_KEY = ['accounting', 'journal-entries'] as const;
const FILES_KEY = ['accounting', 'files'] as const;

export type AccountLedger = { account: AccountData; rows: AccountLedgerRowData[] };

/** Every posted posting that hit an account, oldest first, with a running balance. */
export function useAccountLedger(accountId: number | null) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, 'ledger', accountId],
    enabled: accountId != null,
    queryFn: async (): Promise<AccountLedger> => {
      const { data } = await api.get<AccountLedger>(
        `/v1/accounting/accounts/${accountId}/ledger`,
      );
      return data;
    },
  });
}

export type AccountInput = {
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | 'other_income';
  schedule_c_line?: string | null;
  description?: string | null;
  archived?: boolean;
};

export type JournalLineInput = {
  account_id: number;
  debit_cents: number;
  credit_cents: number;
  memo?: string | null;
};

export type JournalEntryInput = {
  entry_date: string;
  description: string;
  reference?: string | null;
  lines: JournalLineInput[];
  post?: boolean;
};

export function useAccounts(includeArchived = false) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, { includeArchived }],
    queryFn: async (): Promise<AccountData[]> => {
      const { data } = await api.get<AccountData[]>('/v1/accounting/accounts', {
        params: includeArchived ? { include_archived: 1 } : undefined,
      });
      return data;
    },
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AccountInput): Promise<AccountData> => {
      await primeCsrf();
      const { data } = await api.post<AccountData>('/v1/accounting/accounts', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<AccountInput> }): Promise<AccountData> => {
      await primeCsrf();
      const { data } = await api.patch<AccountData>(`/v1/accounting/accounts/${id}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/accounts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useJournalEntries(
  filters: {
    status?: 'draft' | 'posted';
    from?: string;
    to?: string;
    q?: string;
    sort?: string;
    page?: number;
  } = {},
) {
  return useQuery({
    queryKey: [...ENTRIES_KEY, filters],
    queryFn: async (): Promise<Paginated<JournalEntryData>> => {
      const { data } = await api.get<Paginated<JournalEntryData>>('/v1/accounting/journal-entries', {
        params: filters,
      });
      return data;
    },
  });
}

export function useCreateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: JournalEntryInput): Promise<JournalEntryData> => {
      await primeCsrf();
      const { data } = await api.post<JournalEntryData>('/v1/accounting/journal-entries', input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTRIES_KEY });
      qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

export function usePostJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JournalEntryData> => {
      await primeCsrf();
      const { data } = await api.post<JournalEntryData>(`/v1/accounting/journal-entries/${id}/post`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTRIES_KEY });
      qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

export function useDeleteJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/journal-entries/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ENTRIES_KEY }),
  });
}

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entryId,
      file,
    }: {
      entryId: number;
      file: File;
    }): Promise<JournalAttachmentData> => {
      await primeCsrf();
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post<JournalAttachmentData>(
        `/v1/accounting/journal-entries/${entryId}/attachments`,
        form,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ENTRIES_KEY }),
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entryId,
      mediaId,
    }: {
      entryId: number;
      mediaId: number;
    }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/journal-entries/${entryId}/attachments/${mediaId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ENTRIES_KEY }),
  });
}

export function useAttachmentFiles(
  filters: {
    orphaned?: boolean;
    q?: string;
    from?: string;
    to?: string;
    sort?: string;
    page?: number;
  } = {},
) {
  return useQuery({
    queryKey: [...FILES_KEY, filters],
    queryFn: async (): Promise<Paginated<AttachmentFileData>> => {
      const { data } = await api.get<Paginated<AttachmentFileData>>('/v1/accounting/files', {
        params: {
          orphaned: filters.orphaned ? 1 : undefined,
          q: filters.q || undefined,
          from: filters.from,
          to: filters.to,
          sort: filters.sort,
          page: filters.page,
        },
      });
      return data;
    },
  });
}

export function useDeleteAttachmentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/accounting/files/${mediaId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FILES_KEY });
      qc.invalidateQueries({ queryKey: ENTRIES_KEY });
    },
  });
}
